import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { keycloak } from './keycloak';
import { fetchSelfProfile, fetchSelfCredentials } from '../api/keycloak-admin';
import { AuthSetupOverlay } from '../components/AuthSetupOverlay';
import { METHOD_CREDENTIAL_TYPE, cacLinkedKey } from '../constants/auth-methods';

/** Minimum token validity in seconds before refresh */
const MIN_TOKEN_VALIDITY_SEC = 30;

/** Interval between token refresh checks in ms */
const TOKEN_REFRESH_INTERVAL_MS = 10_000;

/** Methods that require a popup setup flow */
type SetupMethod = 'cac' | 'mfa' | 'biometric';

interface AuthState {
  authenticated: boolean;
  username: string;
  fullName: string;
  email: string;
  token: string;
  roles: string[];
  /** True when user has no app: roles (awaiting admin approval) */
  isPending: boolean;
}

/** Public auth context consumed by hub pages and the responsive preview. */
export interface AuthContextValue extends AuthState {
  /** Log the user out and redirect to Keycloak */
  logout: () => void;
  /** Check if user has a specific realm role */
  hasRole: (role: string) => boolean;
  /** Whether an auth method setup overlay is active */
  hasSetupPending: boolean;
  /** Re-trigger auth method setup check (e.g. from pending approval page) */
  recheckAuthSetup: () => void;
}

/** Shared auth context. Exported so the local responsive preview can supply a stand-in user. */
export const AuthContext = createContext<AuthContextValue | null>(null);

/** Access the current auth context. Throws if used outside AuthProvider. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

/** Extracts realm roles from the Keycloak token */
function extractRoles(): string[] {
  const realmAccess = keycloak.tokenParsed?.realm_access;
  return realmAccess?.roles ?? [];
}

/**
 * True when the user has no `app:*` roles — i.e., awaiting admin approval.
 *
 * Dev bypass: in `npm run dev` builds only (`import.meta.env.DEV` is stripped
 * to `false` at compile time in production), a localStorage flag + localhost
 * hostname check can short-circuit this to `false` so you can preview the Hub
 * without first approving the account in Keycloak. The branch is dead-code-
 * eliminated by Vite for any staging/prod bundle.
 */
function computeIsPending(roles: string[]): boolean {
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host === '127.0.0.1';
    if (isLocal && localStorage.getItem('vertex-dev-bypass-approval') === '1') {
      return false;
    }
  }
  return !roles.some(r => r.startsWith('app:'));
}

/** Builds full name from token claims */
function extractFullName(): string {
  const given = keycloak.tokenParsed?.given_name ?? '';
  const family = keycloak.tokenParsed?.family_name ?? '';
  const full = `${given} ${family}`.trim();
  return full || (keycloak.tokenParsed?.preferred_username ?? '');
}

/** Wraps children with Keycloak authentication. Redirects to login if unauthenticated. */
export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [authState, setAuthState] = useState<AuthState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingSetup, setPendingSetup] = useState<SetupMethod | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    initKeycloak();
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, []);

  /**
   * Check if a newly registered user needs auth method setup.
   * Sets pendingSetup state instead of redirecting, so the Hub
   * always renders and setup appears as a modal overlay.
   */
  async function checkAuthSetup(): Promise<void> {
    const token = keycloak.token;
    if (!token) return;

    try {
      const user = await fetchSelfProfile(token);
      const attrs = (user.attributes ?? {}) as Record<string, string[]>;
      const preferredMethod = attrs.preferredAuthMethod?.[0];

      if (!preferredMethod) return;

      // CAC: check localStorage for prior linking
      if (preferredMethod === 'cac') {
        const username = keycloak.tokenParsed?.preferred_username ?? '';
        const linked = localStorage.getItem(cacLinkedKey(username));
        if (!linked) {
          setPendingSetup('cac');
        }
        return;
      }

      // MFA / biometric: check if credential already exists
      const expectedCredType = METHOD_CREDENTIAL_TYPE.get(preferredMethod);
      if (!expectedCredType) return;

      const credentials = await fetchSelfCredentials(token);
      const hasMatchingCredential = credentials.some(c => c.type === expectedCredType);
      if (hasMatchingCredential) return;

      if (preferredMethod === 'mfa' || preferredMethod === 'biometric') {
        setPendingSetup(preferredMethod);
      }
    } catch (err) {
      console.warn('[AuthSetup] Check failed:', err);
    }
  }

  /** Re-check auth setup (can be called from pending approval page) */
  function recheckAuthSetup(): void {
    if (keycloak.token) {
      checkAuthSetup();
    }
  }

  /** Initialize Keycloak and set up token refresh */
  async function initKeycloak(): Promise<void> {
    try {
      const authenticated = await keycloak.init({
        onLoad: 'login-required',
        pkceMethod: (window.crypto?.subtle ? 'S256' : false),
        checkLoginIframe: false,
      });

      if (!authenticated) return;

      // Check for pending auth setup BEFORE setting auth state.
      // This ensures pendingSetup is set before the first render,
      // preventing a flash of PendingApprovalView instead of AuthSetupOverlay.
      await checkAuthSetup();

      const roles = extractRoles();
      setAuthState({
        authenticated: true,
        username: keycloak.tokenParsed?.preferred_username ?? '',
        fullName: extractFullName(),
        email: keycloak.tokenParsed?.email ?? '',
        token: keycloak.token ?? '',
        roles,
        isPending: computeIsPending(roles),
      });

      startTokenRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);

      // "Server responded with an invalid status" typically means a stale session
      // or a pending required action (e.g. CONFIGURE_TOTP from an interrupted popup).
      // Clear keycloak-js state and force a fresh login instead of showing an error.
      if (message.includes('invalid status')) {
        console.warn('[Auth] Stale session detected, forcing fresh login:', message);
        keycloak.clearToken();
        keycloak.login();
        return;
      }

      setError(`Authentication failed: ${message}`);
    }
  }

  /** Periodically refresh the token before it expires */
  function startTokenRefresh(): void {
    refreshTimerRef.current = setInterval(async () => {
      try {
        const refreshed = await keycloak.updateToken(MIN_TOKEN_VALIDITY_SEC);
        if (refreshed) {
          const updatedRoles = extractRoles();
          setAuthState(prev =>
            prev
              ? { ...prev, token: keycloak.token ?? '', roles: updatedRoles, isPending: computeIsPending(updatedRoles) }
              : prev,
          );
        }
      } catch {
        keycloak.login();
      }
    }, TOKEN_REFRESH_INTERVAL_MS);
  }

  const logout = useCallback(() => {
    const logoutUrl = new URL(
      `/realms/${keycloak.realm}/protocol/openid-connect/logout`,
      keycloak.authServerUrl,
    );
    logoutUrl.searchParams.set('client_id', 'vertex-hub');
    logoutUrl.searchParams.set('post_logout_redirect_uri', window.location.origin);
    if (keycloak.idToken) {
      logoutUrl.searchParams.set('id_token_hint', keycloak.idToken);
    }
    window.location.href = logoutUrl.toString();
  }, []);

  if (error) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', fontFamily: 'sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Authentication Error</h1>
          <p style={{ color: '#888' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!authState) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', fontFamily: 'sans-serif' }}>
        <p style={{ color: '#888' }}>Authenticating...</p>
      </div>
    );
  }

  const hasRole = (role: string): boolean => authState.roles.includes(role);

  /** Setup completed — dismiss overlay */
  function handleSetupComplete(): void {
    if (pendingSetup === 'cac' && authState) {
      localStorage.setItem(cacLinkedKey(authState.username), 'true');
    }
    setPendingSetup(null);
  }

  /** Setup skipped — dismiss overlay, mark as skipped for CAC */
  function handleSetupSkip(): void {
    if (pendingSetup === 'cac' && authState) {
      localStorage.setItem(cacLinkedKey(authState.username), 'skipped');
    }
    setPendingSetup(null);
  }

  /** User changed auth method from the setup page */
  function handleChangeMethod(method: SetupMethod): void {
    setPendingSetup(method);
  }

  return (
    <AuthContext.Provider value={{ ...authState, logout, hasRole, hasSetupPending: pendingSetup !== null, recheckAuthSetup }}>
      {/* Full-page auth setup replaces the Hub until complete */}
      {pendingSetup ? (
        <AuthSetupOverlay
          method={pendingSetup}
          username={authState.username}
          onComplete={handleSetupComplete}
          onSkip={handleSetupSkip}
          onChangeMethod={handleChangeMethod}
        />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}
